import { useTranslation } from 'react-i18next'
import { Span } from '@/components/atoms'
import React from 'react'
import { useStepper } from './stepper.hook'

export interface StepperItem {
  label: string
  children: React.ReactNode
}

interface StepperProps {
  items: StepperItem[]
  currentStep: number
  className?: string
  dynamicHeight?: boolean
}

export function Stepper({
  items,
  currentStep,
  className = '',
  dynamicHeight = false,
}: StepperProps) {
  const { t } = useTranslation()
  const {
    step,
    currentLabel,
    panelRefs,
    height,
    isSliding,
    handleTransitionEnd,
  } = useStepper({ items, currentStep, dynamicHeight })

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex w-full max-w-sm flex-col gap-2">
        <Span
          className="tracking-base min-w-max text-xs leading-4 text-neutral-900"
          data-testid="stepper-indicator"
        >
          {step + 1} {t('common.of')} {items.length} {currentLabel}
        </Span>
        <div className="flex w-1/2 max-w-sm gap-0.75">
          {items.map((_, i) => (
            <div
              key={i}
              className={[
                'h-1 min-w-px flex-1 rounded-full transition',
                i <= step ? 'bg-coral-base' : 'bg-coral-100',
              ].join(' ')}
            />
          ))}
        </div>
      </div>

      <div
        className={
          dynamicHeight
            ? isSliding
              ? 'overflow-x:clip'
              : ''
            : 'flex-1 overflow-hidden'
        }
        style={
          dynamicHeight
            ? {
                height: height !== undefined ? `${height}px` : 'auto',
                transition: `height var(--default-transition-duration) var(--default-transition-timing-function)`,
              }
            : undefined
        }
      >
        <div
          className={`flex ${dynamicHeight ? 'items-start' : 'h-full'}`}
          style={{
            transform: `translateX(-${step * 100}%)`,
            transition: `transform var(--default-transition-duration) var(--default-transition-timing-function)`,
          }}
          onTransitionEnd={(e) => handleTransitionEnd(e.propertyName)}
        >
          {items.map((item, i) => (
            <div
              key={i}
              ref={(el) => {
                panelRefs.current[i] = el ?? undefined
              }}
              className={`w-full shrink-0 px-0.5 transition ${i === step ? '' : 'opacity-0'}`}
            >
              {item.children}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
